import * as React from "react";
import { cn } from "../../lib/utils";
import { TabItem, type TabItemProps } from "../TabItem/TabItem";

export interface Tab {
  /**
   * Unique identifier for the tab
   */
  id: string | number;
  /**
   * Leading component (e.g., icon)
   */
  leadingComponent?: React.ReactNode;
  /**
   * Tab title/label
   */
  title: string;
  /**
   * Trailing component (e.g., badge, close button)
   */
  trailingComponent?: React.ReactNode;
  /**
   * Whether the tab is disabled
   */
  isDisabled?: boolean;
  /**
   * Tab content to render when selected
   */
  content?: React.ReactNode;
}

export interface TabsProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  /**
   * Array of tab configurations
   */
  tabs: Tab[];
  /**
   * Currently selected tab ID
   */
  selectedTabId?: string | number;
  /**
   * Default selected tab ID (for uncontrolled mode)
   */
  defaultSelectedTabId?: string | number;
  /**
   * Callback when tab selection changes
   */
  onTabChange?: (tabId: string | number) => void;
  /**
   * Tab visual variant
   */
  variant?: "bordered" | "borderless";
  /**
   * Tab size
   */
  size?: "small" | "medium" | "large";
  /**
   * Whether tabs should take full width
   */
  isFullWidth?: boolean;
  /**
   * Custom class name for the tabs container
   */
  className?: string;
  /**
   * Custom class name for the tab list
   */
  tabListClassName?: string;
  /**
   * Custom class name for the tab content area
   */
  contentClassName?: string;
  /**
   * Whether to render tab content
   */
  renderContent?: boolean;
}

const Tabs = React.forwardRef<HTMLDivElement, TabsProps>(
  (
    {
      tabs,
      selectedTabId,
      defaultSelectedTabId,
      onTabChange,
      variant = "bordered",
      size = "large",
      isFullWidth = false,
      className,
      tabListClassName,
      contentClassName,
      renderContent = true,
      ...props
    },
    ref
  ) => {
    // Internal state for uncontrolled mode
    const [internalSelectedId, setInternalSelectedId] = React.useState<
      string | number
    >(defaultSelectedTabId || tabs[0]?.id || "");

    // Determine if component is controlled
    const isControlled = selectedTabId !== undefined;
    const activeTabId = isControlled ? selectedTabId : internalSelectedId;

    // Handle tab selection
    const handleTabClick = (tabId: string | number) => {
      if (!isControlled) {
        setInternalSelectedId(tabId);
      }
      onTabChange?.(tabId);
    };

    // Get the active tab content
    const activeTab = tabs.find((tab) => tab.id === activeTabId);

    return (
      <div ref={ref} className={cn("w-full", className)} {...props}>
        {/* Tab List */}
        <div
          role="tablist"
          className={cn(
            "inline-flex items-center gap-4",
            {
              "w-full": isFullWidth,
              "border-b border-b-[1.5px] border-surface-outline-neutral-muted":
                variant === "bordered",
            },
            tabListClassName
          )}
        >
          {tabs.map((tab) => (
            <TabItem
              key={tab.id}
              id={String(tab.id)}
              leadingComponent={tab.leadingComponent}
              title={tab.title}
              trailingComponent={tab.trailingComponent}
              isSelected={tab.id === activeTabId}
              isDisabled={tab.isDisabled}
              variant={variant}
              size={size}
              isFullWidth={isFullWidth}
              onClick={() => !tab.isDisabled && handleTabClick(tab.id)}
            />
          ))}
        </div>

        {/* Tab Content */}
        {renderContent && activeTab?.content && (
          <div
            role="tabpanel"
            className={cn("mt-4", contentClassName)}
            aria-labelledby={String(activeTabId)}
          >
            {activeTab.content}
          </div>
        )}
      </div>
    );
  }
);

Tabs.displayName = "Tabs";

export { Tabs };
